/**
 * SubscriptionPage — Paket C (premium-checkout-redesign, subscription.html)
 *
 * Route: /account/subscription
 * Routing-Weiche:
 *   - Kein Abo (null)  → PlanSelection
 *   - Aktives Abo      → Hero (ActiveSubscription) + zweispaltiges Layout
 *                         (Tarif wechseln / Zahlungsmethode+Historie+Kündigen)
 * Toast-Feedback für alle Aktionen.
 */
import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useCryptoPrices } from '../../hooks/useCryptoPrices'
import { useSubscription } from './useSubscription'
import { useSubscriptionPlans } from './useSubscriptionPlans'
import type { Plan } from './useSubscriptionPlans'
import { PlanSelection } from './PlanSelection'
import { ActiveSubscription } from './ActiveSubscription'
import { PlanChangeCalculator } from './PlanChangeCalculator'
import { CancelFlowModal } from './CancelFlowModal'
import { SubscriptionHistory } from './SubscriptionHistory'
import { BillingCard, CancelCard, freeTierQuotaNumber } from './SubscriptionSidebarCards'
import { formatSubscriptionDate } from './subscriptionUtils'
import { findCatalogPlan } from '../premium/legacyPlanMap'
import { planDisplayName } from '../premium/planDisplayName'
import { planQuotas, PLAN_QUOTA_FALLBACK } from '../premium/planQuotas'
import { usePreviewUpgrade } from '../premium/usePreviewUpgrade'
import { useAccount } from '../settings/useAccount'
import { planRank, convertCreditToDays, remainingValue } from '../premium/planMath'
import './subscription.css'

// --- Toast ---

type ToastState = {
  message: string
  type: 'success' | 'error'
} | null

function Toast({ toast, onDismiss }: { toast: ToastState; onDismiss: () => void }) {
  const { t } = useTranslation('subscription')
  if (!toast) return null
  const bg = toast.type === 'success' ? 'var(--success, #10b981)' : 'var(--danger, #ef4444)'

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="toast"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 2000,
        background: bg,
        color: '#fff',
        padding: '12px 20px',
        borderRadius: 8,
        fontSize: 14,
        maxWidth: 360,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      }}
    >
      <span>{toast.message}</span>
      <button
        onClick={onDismiss}
        style={{
          background: 'none',
          border: 'none',
          color: '#fff',
          cursor: 'pointer',
          fontSize: 18,
          lineHeight: 1,
        }}
        aria-label={t('page.toast.close')}
      >
        ×
      </button>
    </div>
  )
}

// --- Page ---

type SubscriptionPageProps = {
  // Deep-Link aus /plans bzw. /account/subscription?plan=<id> — nur relevant
  // solange kein aktives Abo besteht (PlanSelection).
  initialPlanId?: string
  // Story 6.3: auf /plans gewaehlte Laufzeit (1|3|6|12) wird in die
  // Plan-Auswahl uebernommen — der bestehende Stripe-Checkout bleibt unveraendert.
  initialDuration?: 1 | 3 | 6 | 12
  // Deep-Link ?change=<plan_id> — Vorauswahl des Rechners.
  initialChangePlanId?: string
}

function daysRemainingFallback(currentPeriodEnd: string): number {
  return Math.max(0, Math.ceil((new Date(currentPeriodEnd).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
}

/** Nächster Downgrade-Kandidat (höchster Rang unterhalb des aktuellen Plans), sonst 'ideal', sonst der erste Katalogplan. */
function defaultChangeTargetId(plans: Plan[], currentPlanId: string): string | undefined {
  const catalog = plans.filter((p) => p.id !== currentPlanId && p.id !== 'free')
  if (catalog.length === 0) return undefined
  const currentRank = planRank(plans, currentPlanId)
  const downCandidates = catalog
    .map((p) => ({ id: p.id, rank: planRank(plans, p.id) }))
    .filter((x) => x.rank >= 0 && x.rank < currentRank)
    .sort((a, b) => b.rank - a.rank)
  if (downCandidates[0]) return downCandidates[0].id
  return catalog.find((p) => p.id === 'ideal')?.id ?? catalog[0].id
}

export function SubscriptionPage({ initialPlanId, initialDuration, initialChangePlanId }: SubscriptionPageProps = {}) {
  const { t } = useTranslation('subscription')
  const {
    subscription,
    isLoading,
    isError,
    isMutating,
    refetch,
    subscribe,
    cancel,
    resume,
    changePlan,
    updatePaymentMethod,
    requestCryptoQuote,
  } = useSubscription()

  // F2: gleiche Anzeige-Waehrung wie in PlanSelection/PlansAndApiPage — sonst
  // zeigt die Verwaltungsansicht einen Plan aus einem anderen Katalog.
  const { currency } = useCryptoPrices()
  const { plans } = useSubscriptionPlans(currency)
  const { account } = useAccount()

  // Resolve current plan by plan_id from subscription.
  // P1-1: zentrales Legacy-Plan-Mapping statt rohem `.find()` — Alt-Plan-IDs
  // (z.B. "premium" aus v1) matchen sonst keinen der 9 SPEC-Tarife.
  const currentPlan = subscription ? findCatalogPlan(plans, subscription.plan_id) : null

  // Abgelaufenes Abo: keine Verwaltungs-Aktionen (Kuendigen/Plan-Wechsel) auf einem
  // toten Abo anbieten — stattdessen Reaktivierung ueber die Plan-Auswahl.
  const isExpired = subscription?.status === 'expired'
  const expiredDate = isExpired ? formatSubscriptionDate(subscription.current_period_end) : null

  const [toast, setToast] = useState<ToastState>(null)
  const [showCancelFlow, setShowCancelFlow] = useState(false)

  // Rechner-Ziel: Deep-Link ?change= zuerst, sonst nächster Downgrade-
  // Kandidat bzw. 'ideal'. Wird einmal gesetzt, sobald Katalog + aktueller
  // Plan geladen sind (Ref-Guard verhindert ein Überschreiben späterer
  // manueller Auswahl bei Re-Renders).
  const [targetPlanId, setTargetPlanId] = useState<string>('')
  const targetInitialized = useRef(false)
  useEffect(() => {
    if (targetInitialized.current) return
    if (!plans || !currentPlan) return
    const initial =
      (initialChangePlanId && plans.some((p) => p.id === initialChangePlanId) && initialChangePlanId !== currentPlan.id
        ? initialChangePlanId
        : undefined) ?? defaultChangeTargetId(plans, currentPlan.id)
    if (initial) {
      // Deep-Link-Vorauswahl kann erst berechnet werden, sobald Katalog +
      // aktueller Plan asynchron geladen sind; der Ref-Guard sorgt dafür,
      // dass genau einmal gesetzt wird (spätere manuelle Auswahl bleibt
      // unangetastet).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTargetPlanId(initial)
      targetInitialized.current = true
    }
  }, [plans, currentPlan, initialChangePlanId])

  // Gemeinsame Vorschau für Hero-Restwert UND Rechner (BE-Konvention:
  // proration_credit/days_remaining/converted_days/new_expires_at je Zielplan).
  const { previewUpgrade } = usePreviewUpgrade()
  const preview = useQuery({
    queryKey: ['subscription', 'preview_upgrade', targetPlanId],
    queryFn: () => previewUpgrade(targetPlanId),
    enabled: !!subscription && targetPlanId !== '' && targetPlanId !== currentPlan?.id,
    staleTime: 30_000,
  })

  const showToast = useCallback(
    (message: string, type: 'success' | 'error' = 'success') => {
      setToast({ message, type })
      const timer = setTimeout(() => setToast(null), 5000)
      return () => clearTimeout(timer)
    },
    [setToast],
  )

  const handleSubscribeSuccess = useCallback(() => {
    showToast(t('page.toast.subscribeSuccess'))
    refetch()
  }, [showToast, refetch, t])

  const handleAlreadySubscribed = useCallback(() => {
    showToast(t('page.toast.alreadySubscribed'), 'error')
    refetch()
  }, [showToast, refetch, t])

  if (isLoading) {
    return (
      <div
        aria-busy="true"
        aria-label={t('page.loading.ariaLabel')}
        data-testid="subscription-loading"
      >
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-subtle)' }}>
          {t('page.loading.text')}
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <div role="alert" className="empty-state" data-testid="subscription-error">
        <p>{t('page.error.text')}</p>
        <button className="btn btn-primary" onClick={refetch}>
          {t('common:actions.retry')}
        </button>
      </div>
    )
  }

  const showActive = subscription != null && !isExpired

  // Downgrade-Vorschlag + 12-Monats-Angebot für den Kündigungs-Dialog.
  const smallerPlanId = currentPlan ? defaultChangeTargetId(plans ?? [], currentPlan.id) : undefined
  const smallerPlan = smallerPlanId ? (plans ?? []).find((p) => p.id === smallerPlanId) ?? null : null
  const smallerIsDowngrade =
    smallerPlan && currentPlan ? planRank(plans ?? [], smallerPlan.id) < planRank(plans ?? [], currentPlan.id) : false
  const creditForCancelFlow =
    currentPlan && subscription
      ? remainingValue(currentPlan.price_eur_monthly, daysRemainingFallback(subscription.current_period_end))
      : 0
  const smallerPlanMonths =
    smallerPlan && smallerIsDowngrade
      ? convertCreditToDays(creditForCancelFlow, smallerPlan.price_eur_monthly) / 30.44
      : null
  const twelveMonthEntry = currentPlan?.durations?.find((d) => d.months === 12)
  const twelveMonthMonthly =
    twelveMonthEntry && typeof twelveMonthEntry.price === 'number' ? twelveMonthEntry.price / 12 : null

  const freePlan = plans?.find((p) => p.id === 'free') ?? null
  const freeQuotas = freePlan ? planQuotas(freePlan) : PLAN_QUOTA_FALLBACK.free
  const freeMobile = freeTierQuotaNumber(freeQuotas.mobile, PLAN_QUOTA_FALLBACK.free.mobile as number)
  const freeIot = freeTierQuotaNumber(freeQuotas.iot, PLAN_QUOTA_FALLBACK.free.iot as number)

  return (
    <div data-testid="subscription-page">
      {/* Fix P2 (Abnahme 2026-09-01): Breadcrumb "Account > My Subscription"
          (Referenz subscription.html: HtLayout.mount breadcrumb: [{label:'Account'},{label:'My Subscription'}]),
          gleiches Muster wie PaymentsPage.tsx (payments-breadcrumb). */}
      <nav
        className="subscription-breadcrumb"
        aria-label="Breadcrumb"
        data-testid="subscription-breadcrumb"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          color: 'var(--text-subtle)',
          marginBottom: 14,
        }}
      >
        <span>{t('shell:nav.sections.account')}</span>
        <span aria-hidden="true">›</span>
        <span style={{ color: 'var(--text)', fontWeight: 500 }}>{t('page.title')}</span>
      </nav>

      <div className="section-header">
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>{t('page.title')}</h1>
          <p>{t('page.subtitle')}</p>
        </div>
        <Link to="/plans" className="btn" data-testid="view-all-plans-btn">
          {t('page.viewAllPlansButton')}
        </Link>
      </div>

      {isExpired && (
        <div
          role="alert"
          className="alert is-warning"
          data-testid="subscription-expired-hint"
          style={{
            padding: '12px 16px',
            borderRadius: 8,
            marginBottom: 20,
            background: 'var(--warning-bg, #fef3c7)',
            border: '1px solid var(--c-warning, #f59e0b)',
            color: 'var(--warning-text, #92400e)',
          }}
        >
          {expiredDate ? t('page.expiredHint', { date: expiredDate }) : t('page.expiredHintNoDate')}
        </div>
      )}

      {subscription?.cancel_at_period_end && showActive && (
        <div
          role="alert"
          className="alert is-warning"
          data-testid="cancel-banner"
          style={{
            padding: '12px 16px',
            borderRadius: 8,
            marginBottom: 20,
            background: 'var(--warning-bg, #fef3c7)',
            border: '1px solid var(--c-warning, #f59e0b)',
            color: 'var(--warning-text, #92400e)',
          }}
        >
          {t('activeSubscription.cancelBanner', {
            date: formatSubscriptionDate(subscription.current_period_end),
          })}
        </div>
      )}

      {!showActive ? (
        /* Kein (aktives) Abo — Plan-Auswahl bzw. Reaktivierung.
           Bewusst ohne Preselect des abgelaufenen Plans: initialPlanId oeffnet
           das CheckoutModal sofort, der User soll erst den Hinweis lesen. */
        <>
          <PlanSelection
            subscribe={subscribe}
            requestCryptoQuote={requestCryptoQuote}
            refetchSubscription={refetch}
            onSubscribeSuccess={handleSubscribeSuccess}
            onAlreadySubscribed={handleAlreadySubscribed}
            initialPlanId={isExpired ? undefined : initialPlanId}
            initialDuration={initialDuration}
          />
          {/* Premium-Historie: bewusst auch fuer Free-/Expired-User sichtbar. */}
          <SubscriptionHistory />
        </>
      ) : (
        <>
          <div style={{ marginBottom: 20 }}>
            <ActiveSubscription
              subscription={subscription}
              currentPlan={currentPlan}
              preview={preview.data}
              previewFetching={preview.isFetching}
            />
          </div>

          <div className="sub-grid">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
              {currentPlan && targetPlanId && (
                <PlanChangeCalculator
                  currentPlan={currentPlan}
                  availablePlans={plans ?? []}
                  subscription={subscription}
                  hotspotQuota={account?.hotspot_quota}
                  targetPlanId={targetPlanId}
                  onTargetPlanChange={setTargetPlanId}
                  preview={preview.data}
                  previewFetching={preview.isFetching}
                  changePlan={changePlan}
                  isMutating={isMutating}
                  onSuccess={(msg) => {
                    showToast(msg, 'success')
                    refetch()
                  }}
                  onError={(msg) => showToast(msg, 'error')}
                />
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
              <BillingCard
                subscription={subscription}
                onUpdatePaymentMethod={updatePaymentMethod}
                onPaymentMethodUpdated={() => showToast(t('page.toast.paymentMethodUpdated'))}
              />
              <div className="card card-pad">
                <div className="spread" style={{ marginBottom: 4 }}>
                  <h3 style={{ margin: 0, fontSize: 14 }}>{t('historyCard.title')}</h3>
                  <a href="/payments" className="btn btn-sm btn-ghost" data-testid="all-invoices-btn">
                    {t('historyCard.allInvoicesButton')}
                  </a>
                </div>
                <SubscriptionHistory activeSubscription={subscription} activePlanLabel={currentPlan?.name} />
              </div>
              <CancelCard
                subscription={subscription}
                freePlan={freePlan}
                isMutating={isMutating}
                onOpenCancelFlow={() => setShowCancelFlow(true)}
                onResume={resume}
                onSuccess={(msg: string) => showToast(msg, 'success')}
                onError={(msg) => showToast(msg, 'error')}
              />
            </div>
          </div>
        </>
      )}

      {showCancelFlow && subscription && (
        <CancelFlowModal
          currentPlanName={currentPlan?.name ?? planDisplayName(subscription.plan_id) ?? subscription.plan_id}
          currentPlanId={currentPlan?.id ?? subscription.plan_id}
          periodEndLabel={formatSubscriptionDate(subscription.current_period_end)}
          currency={currentPlan?.currency ?? 'usd'}
          creditAmount={creditForCancelFlow}
          smallerPlanId={smallerIsDowngrade ? smallerPlan?.id ?? null : null}
          smallerPlanName={smallerIsDowngrade ? smallerPlan?.name ?? null : null}
          smallerPlanMonths={smallerPlanMonths}
          twelveMonthMonthly={twelveMonthMonthly}
          freeMobile={freeMobile}
          freeIot={freeIot}
          isMutating={isMutating}
          onSelectDowngrade={(planId) => {
            setTargetPlanId(planId)
            setShowCancelFlow(false)
            document.getElementById('change')?.scrollIntoView({ behavior: 'smooth' })
          }}
          onCancelConfirmed={async () => {
            try {
              await cancel()
              showToast(t('activeSubscription.cancel.success', {
                date: formatSubscriptionDate(subscription.current_period_end),
              }))
            } catch {
              showToast(t('activeSubscription.cancel.error'), 'error')
              throw new Error('cancel failed')
            }
          }}
          onClose={() => setShowCancelFlow(false)}
        />
      )}

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  )
}
